/**
 * Google Maps URL validation — the shape the business-location link field
 * accepts.
 *
 * The config form's map link is not a free-text URL: it is handed to the
 * coordinate resolver (which parses `@lat,lng` / `!3d!4d` / `?q=` shapes and
 * expands `maps.app.goo.gl` short links) and it is what the assistant sends a
 * customer who asks for directions. A link to anywhere else resolves to no
 * coordinates and sends the customer to the wrong place, so a plain `z.url()`
 * is too loose — an arbitrary URL is as wrong here as free text.
 *
 * Accepted:
 *   - `https://maps.app.goo.gl/…`            (modern short share link)
 *   - `https://goo.gl/maps/…`                (legacy short share link)
 *   - `https://maps.google.<tld>/…`          (maps host, any path)
 *   - `https://www.google.<tld>/maps…`       (any Google host, `/maps` path)
 *
 * Regional Google domains (`google.co.uk`, `google.com.sa`, …) are accepted:
 * a share link copied from a localized Google Maps carries the local TLD.
 *
 * NOTE: the server keeps its own copy of the Google-host logic in
 * `domain/entities/app-config/google-maps-location.ts` — the domain may not
 * import api-client, and that copy answers a different question (which hosts the
 * short-link expander may DIAL, an SSRF allowlist). This one answers "would a
 * human call this a Google Maps link?".
 */

import { z } from "zod";

/** Human-facing reason, reused by the form schema and the write boundary. */
export const GOOGLE_MAPS_URL_MESSAGE =
	"Enter a Google Maps link (for example https://maps.app.goo.gl/… or https://www.google.com/maps?q=…).";

/** `google.com`, `www.google.com`, `maps.google.co.uk`, … — any Google host. */
const GOOGLE_HOST_RE = /^(?:[a-z0-9-]+\.)*google\.[a-z]{2,}(?:\.[a-z]{2,})?$/;

/** Parse a URL, returning `null` instead of throwing on malformed input. */
function safeUrl(input: string): URL | null {
	try {
		return new URL(input.trim());
	} catch {
		return null;
	}
}

/**
 * Whether `value` is a Google Maps link the coordinate resolver can work with.
 * Non-string, malformed, and non-http(s) input is rejected rather than thrown on,
 * so this is safe to use directly as a Zod refinement.
 */
export function isGoogleMapsUrl(value: unknown): boolean {
	if (typeof value !== "string") {
		return false;
	}
	const url = safeUrl(value);
	if (!url) {
		return false;
	}
	if (url.protocol !== "https:" && url.protocol !== "http:") {
		return false;
	}
	const host = url.host.toLowerCase();
	const path = url.pathname.toLowerCase();
	if (host === "maps.app.goo.gl") {
		// The short link's own path is the opaque id — any non-empty one is valid.
		return path.length > 1;
	}
	if (host === "goo.gl") {
		return path.startsWith("/maps");
	}
	if (!GOOGLE_HOST_RE.test(host)) {
		return false;
	}
	// A dedicated maps host is a maps link at any path (`maps.google.com/?q=…`);
	// every other Google host must actually point at the Maps product.
	return host.startsWith("maps.google.") || path.startsWith("/maps");
}

/**
 * A non-empty Google Maps link. Compose with `.nullable()` / `.optional()` at
 * the call site — a business that has not set a map link stores `null`, which is
 * a valid, saveable state (the link is NOT a required field).
 */
export const GoogleMapsUrlSchema = z
	.string()
	.trim()
	.min(1, GOOGLE_MAPS_URL_MESSAGE)
	.refine(isGoogleMapsUrl, { message: GOOGLE_MAPS_URL_MESSAGE });
